import atlas from "./atlas.json";
import manifest from "./models.json";

// The Blender-built models (scripts/kart-models/build_models.py): the seven
// roster characters, the bodies and karts they drive, the items — and
// fetching them on demand, versioned by content.

export type KartKind = keyof typeof manifest.karts;
export type CharKey = keyof typeof manifest.roster;
export type BodyKind = keyof typeof manifest.bodies;

/** The roster, in the order the character select shows it. */
export const ROSTER = manifest.roster;
export const ROSTER_KEYS = Object.keys(manifest.roster) as CharKey[];
/** Each body type: where its head sits (x, y in the kart's axes) — the head models are built there. */
export const BODIES = manifest.bodies;

/** The expressions every character's head carries, and the arm poses every body does (index ranges in their files). */
export type FaceState = "neutral" | "focus" | "dizzy" | "joy" | "sad";
export type Pose = "drive" | "left" | "right" | "cheer" | "slump";

/**
 * The character a racer drives: the one they picked, else the one they're
 * named for — by a whole word, so "AC" is AC and not every Jacob.
 */
export function characterOf(r: { name: string; char?: string }): CharKey | null {
  if (r.char && r.char in manifest.roster) return r.char as CharKey;
  const words = r.name.toLowerCase().split(/[^a-zæøå0-9]+/);
  return ROSTER_KEYS.find((k) => words.includes(k)) ?? null;
}

export interface ModelData {
  vertices: number;
  indices: number;
  data: ArrayBuffer;
  /** For a file of several parts: each part's first index and index count. */
  ranges?: Record<string, number[]>;
}

export type AtlasName = keyof typeof atlas;

/**
 * Where an image is in its texture atlas (scripts/kart-models/pack_atlas.py):
 * the atlas's URL — versioned by its content — and size, and the image's
 * rectangle in it. "roster" has the characters' portraits (by key); "hud"
 * their icons (`char-<key>`) and the item icons (`item-<kind>`).
 */
export function sprite(name: AtlasName, frame: string) {
  const a = atlas[name];
  const rect = (a.frames as Record<string, number[]>)[frame];
  return rect ? { url: `${process.env.PUBLIC_URL}/kart/atlas/${a.file}`, size: a.size, rect } : null;
}

/** Fetched and decoded up front, so an icon that mounts mid-race paints at once; held so they stay decoded. */
const atlasImages: HTMLImageElement[] = [];
export function preloadAtlases() {
  if (typeof Image === "undefined" || atlasImages.length) return;
  for (const a of Object.values(atlas)) {
    const img = new Image();
    img.src = `${process.env.PUBLIC_URL}/kart/atlas/${a.file}`;
    img.decode?.().catch(() => {});
    atlasImages.push(img);
  }
}

const cache = new Map<string, Promise<ModelData | null>>();

/** Models that couldn't be had, and why — shown in the menu, so a failure on a phone can be read off the screen. */
export const modelFailures = new Map<string, string>();

type Info = { vertices: number; indices: number; hash: string; ranges?: Record<string, number[]> };
/** Every model by file name: `kart-classic`, `body-S`, `char-zobbe`, `item-glove`… */
const MODELS: Record<string, Info> = Object.fromEntries(
  (
    [
      ["kart", manifest.karts],
      ["body", manifest.bodies],
      ["char", manifest.roster],
      ["item", manifest.items],
    ] as [string, Record<string, Info>][]
  ).flatMap(([group, entries]) => Object.entries(entries).map(([key, info]) => [`${group}-${key}`, info])),
);

/** Fetches a model by file name. Resolves null if it can't be had. */
export function loadModel(name: string): Promise<ModelData | null> {
  const info = MODELS[name];
  if (!info) return Promise.resolve(null);
  if (!cache.has(name)) {
    const expected = info.vertices * 20 + info.indices * 2;
    const get = (url: string) =>
      fetch(url)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((data) => {
          if (data.byteLength !== expected) throw new Error(`${data.byteLength} bytes, expected ${expected}`);
          return { vertices: info.vertices, indices: info.indices, ranges: info.ranges, data };
        });
    // Versioned by content: the service worker serves files stale-while-
    // revalidate, and an old copy of a rebuilt model under the same URL is
    // the wrong size for this manifest — which used to leave every driver on
    // the fallback until the cache happened to catch up. Should anything
    // still come back wrong, one more try from the network, past every cache.
    const url = `${process.env.PUBLIC_URL}/kart/models/${name}.bin?v=${info.hash}`;
    cache.set(
      name,
      get(url)
        .catch(() => get(`${url}&fresh=${Date.now()}`))
        .catch((e: Error) => {
          modelFailures.set(name, e.message);
          return null;
        }),
    );
  }
  return cache.get(name)!;
}
